import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, Plane, Building, Navigation, Globe } from "lucide-react";
import StandardSection from "./ui/StandardSection";
import { assetUrl } from "@/utils/assetUrl";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/utils/authContext";

// Static config for icons, colors, and JSON keys
const coverageConfig = [
  {
    icon: MapPin,
    color: "text-primary-600",
    key: "global_coverage_item1_label",
  },
  {
    icon: Plane,
    color: "text-forest-custom",
    key: "global_coverage_item2_label",
  },
  {
    icon: Building,
    color: "text-primary-600",
    key: "global_coverage_item3_label",
  },
  {
    icon: Navigation,
    color: "text-forest-custom",
    key: "global_coverage_item4_label",
  },
];

// Type for the dynamic feature items
interface CoverageFeature {
  icon: React.ElementType;
  label: string;
  color: string;
}

interface CoverageUSPProps {
  isLoginEnabled: boolean;
  isExploreEnabled: boolean;
  base_path: string;
}

const DEFAULT_ALT_TEXT = "Times Square Billboard Advertising";

export default function CoverageUSP({
  isLoginEnabled,
  isExploreEnabled,
  base_path
}: CoverageUSPProps) {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  // Set the fallback image using the original assetUrl function
  const fallbackImage = assetUrl("times-square-5913240_1755604729399.jpg");

  // State for dynamic content
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionPrimaryHeader, setSectionPrimaryHeader] = useState("");
  const [sectionDescription, setSectionDescription] = useState("");
  const [coverageItems, setCoverageItems] = useState<CoverageFeature[]>([]);
  
  // --- UPDATED LOGIC ---
  // Initialize state with the fallback values
  const [sectionImage, setSectionImage] = useState(fallbackImage);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const value = data.find((page: any) => page.name === "Landing Page");

        if (!value) {
          throw new Error('Landing page content not found');
        }

        const coveragePanel = value?.fields?.find((field: any) => field.key === "globalCoveragePanel");

        if (coveragePanel) {
          // Set main section content
          setSectionTitle(coveragePanel.components?.find((c: any) => c.key === "global_coverage_title")?.defaultValue || "");
          setSectionPrimaryHeader(coveragePanel.components?.find((c: any) => c.key === "global_coverage_primary_header")?.defaultValue || "");
          setSectionDescription(coveragePanel.components?.find((c: any) => c.key === "global_coverage_description")?.defaultValue || "");
          // Build the new coverage items array
          const newItems = coverageConfig
            .map(config => {
              const label = coveragePanel.components?.find((c: any) => c.key === config.key)?.defaultValue || "";
              return {
                icon: config.icon,
                label: label,
                color: config.color,
              };
            })
            .filter(item => item.label && item.label.trim() !== "");

          setCoverageItems(newItems);

          // Find and set the section image
          const imageGrid = coveragePanel.components?.find((c: any) => c.key === "globalCoverageImageGrid");
          const imageFileData = imageGrid?.defaultValue?.[0]?.globalCoverageImage;

          if (imageFileData?.length > 0) {
            const imageFile = imageFileData[0];
            // Apply the requested logic checking .url and then .data.url
            const imgUrl = imageFile.url
              ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${imageFile.url}`
              : imageFile.data?.url
                ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${imageFile.data.url}`
                : null; // Use null if no dynamic URL found

            if (imgUrl) {
              setSectionImage(imgUrl); // Set dynamic image
            } else {
              setSectionImage(fallbackImage); // Re-set to fallback if data is bad
            }
          }
          // No need for the misleading console.log here
        }
      } catch (error) {
        console.error("Error fetching content for CoverageUSP:", error);
        // Set to empty on error
        setSectionTitle("");
        setSectionPrimaryHeader("");
        setSectionDescription("");
        setCoverageItems([]);
        // --- UPDATED LOGIC ---
        // Explicitly set fallbacks on error
        setSectionImage(fallbackImage); 
      }
    };

    fetchContent();
  }, [fallbackImage]); // Add fallbackImage as a dependency

  const handleFindBillboard = () => {
    localStorage.setItem('clearStartCampaignFilters', 'true');
    if (!isLoggedIn) {
      navigate(`${base_path}/explore`);
    } else {
      navigate(`${base_path}/propertylist`);
    }
  };

  return (
    <>
      <StandardSection id="coverage" background="light">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-100 to-forest-100 border border-primary-200 text-primary-700 px-6 py-3 rounded-full text-sm mb-8 shadow-sm">
              <Globe className="h-4 w-4 text-primary-600" />
              <span className="text-lg sm:text-xl">
                {sectionTitle}
              </span>
              <MapPin className="h-4 w-4 text-forest-custom" />
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight FontStyles mb-3 sm:mb-4">
              <span className="text-gray-900 block">
                {sectionPrimaryHeader}
              </span>
            </h2>

            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              {sectionDescription}
            </p>
            {/* The grid will now only render items that have a label */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              {coverageItems.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div key={index} className="flex items-center">
                    <IconComponent
                      className={`h-6 w-6 ${feature.color} mr-3`}
                    />
                    <span className="font-semibold text-gray-900">
                      {feature.label}
                    </span>
                  </div>
                );
              })}
            </div>
            {isLoginEnabled && isExploreEnabled && (
              <Button
                onClick={handleFindBillboard}
                size="lg"
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 text-lg font-semibold"
                data-testid="button-find-perfect-billboard"
              >
                Explore Now
              </Button>
            )}
          </div>
          <div className="relative">
            <img
              // --- UPDATED LOGIC ---
              // Now we just use sectionImage, since it's guaranteed to have a value
              src={sectionImage}
              alt={"Times Square Billboard Advertising"}
              className="w-full rounded-3xl shadow-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-green-500/20 rounded-3xl"></div>
          </div>
        </div>
      </StandardSection>
    </>
  );
}